import React, { FC, useState, useEffect, useRef } from 'react';
//components
import Text from 'components/Text';
import Loading from 'components/Loading';
import { Button } from "components/Button";
import toast from 'components/Toast/dispatch';
import { Visibility } from 'components/Visibility';
import Loader from "pages/Quotes/components/generics/Loader";
import Input from "pages/Quotes/components/generics/Input/Input";
import QuotesStockInfo from 'pages/Quotes/components/QuotesStockInfo';
import { AutoMapping } from 'pages/Quotes/components/Tags/AutoMapping';
import Tooltip from 'pages/Quotes/components/generics/Tooltip/Tooltip';
import { VisibilityConditional } from 'components/VisibilityConditional';
import MedicineTypeTag from 'pages/Quotes/components/Tags/MedicineTypeTag';
import ProductSegmentTag from 'pages/Quotes/components/Tags/ProductSegmentTag';
import { RefrigeratedTag } from 'pages/Quotes/components/Tags/RefrigeratedTag';
import PriceHistoryModal from 'pages/Quotes/components/generics/Tooltip/PriceHistoryModal/PriceHistoryModal';
//models
import { ItemStockProps } from 'models/Quote';
import { UpdateApprovedMapAutomaticProps } from 'models/MapProduct';
import { ITRow } from '../interface';
//services
import QuoteServices from "services/quote.service";
import ProductService from 'services/product.service';
// generics
import * as S from '../../styles';
import orderInactive from 'img/icon-order-inactive.svg';
//utils
import { useOutsideClick } from 'hooks/useOutsideClick';
import { currencyOutputNumber } from 'utils/CurrencyOutput';
import { handleCreatePdf } from 'pages/Quotes/Dashboard/utils';
import { onlyNumbersNonNull } from 'pages/Quotes/utils/patterns';
// assets
import removeBtn from "img/cancel-icon.svg";
import confirmBtn from "img/confirm-icon.svg";
import deleteBtn from 'img/delete-icon-new.svg';
import priceHistoryBtn from "img/icon-price-history.svg";
import downloadTechnicalSheetBtn from "img/download-technical-sheet.svg";
//styles
import { colors } from 'pages/Quotes/styles/colors';
import { Column } from 'components/Layout/Grid';
//context
import { useDetailsQuoteContext } from 'contexts/DetailsQuote';

const orderIcon = <img src={orderInactive} alt="Ordernar por coluna" />;

const TRow: FC<ITRow> = ({
  item,
  index,
  cCliente,
  removeItem,
  itemSelected,
  declainedItens,
  approveMapProduct,
  setConversionFactor,
  notifyTooltipToClose,
  setNotifyTooltipToClose,
  endSecondRenderMapProduct
}) => {
  //tooltipTables
  const [isDownloading, setIsDownloading] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [checkDecimals, setDecimalsValue] = useState<any>();
  const [isPriceHistoryTooltipDisplayed, setIsPriceHistoryTooltipDisplayed] = useState(false);
  const [isStockTooltipDisplayed, setIsStockTooltipDisplayed] = useState(false);
  const { sequence } = useDetailsQuoteContext();

  const PRICE_HISTORY_TOOLTIP_INDEX = `history-tooltip-${index}`;
  const STOCK_TOOLTIP_INDEX = `stock-tooltip-${index}`;

  const stock = item.itemStock.filter((stockItem: ItemStockProps) => stockItem.stock > 0);

  const wrapperRef = useRef(null);
  useOutsideClick(wrapperRef, setIsPriceHistoryTooltipDisplayed);

  const handleConversionFactor = (factor: string) => {
    setDecimalsValue(factor);
    setConversionFactor({
      id: item.id,
      factor: factor ?? checkDecimals
    });
  };

  const _productService = new ProductService();

  const handleDownloadTechnicalSheetBtn = async (supplierProductcode: any) => {
    setIsDownloading(true);
    _productService.getDownloadTechnicalSheet(supplierProductcode).then((response: any) => {
      if (response.status === 200) {
        handleCreatePdf(response, "FichaTecnica-Produto", supplierProductcode);
      } else {
        toast.error('Ocorreu um erro durante a requisição');
      }
    }).finally(() => {
      setIsDownloading(false);
    });
  };

  useEffect(() => {
    const { conversionFactor } = item || undefined;

    //received from API
    if (typeof conversionFactor === 'number') {
      setDecimalsValue(
        conversionFactor?.toString().replace(/[.]/, ',')
      );

    } else if (typeof conversionFactor === 'string') {
      const commaIndex = conversionFactor.indexOf(',');

      if (commaIndex > 0) {
        setDecimalsValue(conversionFactor.substr(0, commaIndex + 4));
      } else {
        setDecimalsValue(conversionFactor);
      }
    } else {
      setDecimalsValue(conversionFactor);
    }

  }, [item]);

  const openPriceHistoryTooltip = () => {
    setIsPriceHistoryTooltipDisplayed(!isPriceHistoryTooltipDisplayed);
    !isPriceHistoryTooltipDisplayed && setNotifyTooltipToClose({ closeAll: true, modalToBeOpened: `history-tooltip-${index}` });
    !isPriceHistoryTooltipDisplayed && getGroupPriceHistory();
  };

  useEffect(() => {
    //check for price history tooltip
    setIsPriceHistoryTooltipDisplayed(PRICE_HISTORY_TOOLTIP_INDEX === notifyTooltipToClose.modalToBeOpened);

    //check for stock tooltip
    setIsStockTooltipDisplayed(STOCK_TOOLTIP_INDEX === notifyTooltipToClose.modalToBeOpened);
  }, [notifyTooltipToClose]);

  const areaBottom = (index: number) => {
    if (index <= 2) {
      return '-394%';
    } else {
      return '18%';
    }
  };

  const getGroupPriceHistory = () => {
    new QuoteServices().getGroupPriceHistory(item.code, cCliente)
      .then((res) => {
        const data = res.data.data;
        setTooltipData(data);
      });
  };

  const handleMedicineTypeDescription = (medicineType: string) => {
    if (medicineType && medicineType === 'Ético/marca') {
      return 'Ético/Marca';
    } else if (medicineType && medicineType === 'Generico') {
      return 'Genérico';
    } else {
      return medicineType;
    }
  };

  const notBlocked = sequence === 0 || (sequence === 1 && item.sequentialSuggestion !== "1");

  const renderButtonsActions = () => (
    <S.Cell className="actionsIcons" isAutomatic={item?.isCreatedAutomatically}>
      <Visibility visible={item.itemStock.length > 0 && endSecondRenderMapProduct}>
        <QuotesStockInfo
          mapProducts={true}
          stockInfo={item?.itemStock}
          showExtra={isStockTooltipDisplayed}
          setShowExtra={setIsStockTooltipDisplayed}
        />

        <div className="itemStock stock-column--extra-icon" ref={wrapperRef}>
          {isPriceHistoryTooltipDisplayed &&
            <Tooltip
              areaBottom={areaBottom(index)}
              data-container='body'
              disable
              width={'600px'}
              maxWidth='592px'
              areaRight={'7%'}
              boxShadow='0 4px 23px rgba(120, 120, 120, 120'
            >
              {tooltipData && <PriceHistoryModal data={tooltipData} itemStock={item.itemStock} />}
            </Tooltip>
          }
          <img onClick={openPriceHistoryTooltip} src={priceHistoryBtn} alt="histórico de preços dos itens" />
        </div>
      </Visibility>

      <Visibility visible={!endSecondRenderMapProduct}>
        <Loading onlySpinner size={14} />
      </Visibility>

      <div className="iconsDownloadSheet">
        {isDownloading ?
          <Loader className="downloadLoader" withPadding={false} size={16} />
          :
          <Button tooltip="Baixar ficha técnica" className="IconsDownloadSheetButton" onClick={() => handleDownloadTechnicalSheetBtn(item.supplierProductcode)}>
            <img src={downloadTechnicalSheetBtn} alt='' />
          </Button>
        }
      </div>
      <div className="deleteIcon">
        <img src={deleteBtn} alt="delete" className={(!notBlocked || !endSecondRenderMapProduct) ? "cursor-not-allowed" : ""} onClick={() => (notBlocked || !endSecondRenderMapProduct) && removeItem(item.id)} />
      </div>
    </S.Cell>
  );

  const approvedOrReprovedMapAutomatic = async (isApproved: boolean) => {
    if (isApproved) {
      const data: UpdateApprovedMapAutomaticProps = {
        isApproved,
        supplierProductCode: item.code,
        hospitalProductCode: itemSelected.productCode ?? itemSelected.code ?? "",
        hospitalDocumentNumber: itemSelected.buyerIdentifier ?? ""
      };

      approveMapProduct(item?.id, data);
    } else {

      declainedItens(item);
    }
  };

  const renderButtonsApprovedMaps = () => (
    <S.Cell className="actionsIcons" isAutomatic={item?.isCreatedAutomatically}>
      <Button tooltip="Aprovar De/Para" className="IconsDownloadSheetButton pl-0" onClick={() => approvedOrReprovedMapAutomatic(true)}>
        <img src={confirmBtn} alt='' />
      </Button>
      <Button tooltip="Reprovar De/Para" className="IconsDownloadSheetButton" onClick={() => approvedOrReprovedMapAutomatic(false)}>
        <img src={removeBtn} alt='' />
      </Button>
    </S.Cell>
  );

  return (
    <S.TRow className='rowColor' stock={stock.length > 0}>
      <S.Cell className="icon" isAutomatic={item?.isCreatedAutomatically}>
        <p>{orderIcon}</p>
      </S.Cell>
      <S.Cell className="sequenceNumber" isAutomatic={item?.isCreatedAutomatically}>
        <p>{index + 1}</p>
      </S.Cell>
      <S.Cell className="supplierProductcode" isAutomatic={item?.isCreatedAutomatically}>
        <p>{item.code}</p>
      </S.Cell>
      <S.Cell className="description" isAutomatic={item?.isCreatedAutomatically}>
        <Column className='d-grid'>
          <Visibility visible={item?.isCreatedAutomatically}>
            <AutoMapping />
          </Visibility>
          <Text
            showTip
            size={12}
            showTipContent={item?.description}
          >
            {item?.description}
          </Text>

          <Visibility visible={item?.substance}>
            <Text
              showTip
              size={12}
              color={colors.gray1}
              showTipContent={item?.substance}
            >
              {item?.substance}
            </Text>
          </Visibility>
        </Column>
      </S.Cell>
      <S.Cell className="medicineType" isAutomatic={item?.isCreatedAutomatically}>
        <>
          <div>
            {item.productSegmentDescription &&
              <ProductSegmentTag
                tagText={item.productSegmentDescription.toUpperCase()}
              />
            }
            {item.medicineTypeDescription &&
              <MedicineTypeTag
                tagText={handleMedicineTypeDescription(item.medicineTypeDescription)}
              />
            }
          </div>
          <RefrigeratedTag refrigerated={item?.refrigeration === "SIM"} />
        </>
      </S.Cell>
      <S.Cell className="subUnit" isAutomatic={item?.isCreatedAutomatically}>
        <p>{item.subUnit}</p>
      </S.Cell>
      <S.Cell className="unit" isAutomatic={item?.isCreatedAutomatically}>
        <p>{item.unit}</p>
      </S.Cell>
      <S.Cell className="conversionFactor" isAutomatic={item?.isCreatedAutomatically}>
        <Input
          width="60px"
          type="number"
          isValid={true}
          setIsValid={() => { }}
          pattern={onlyNumbersNonNull}
          alt='inputConversionFactorInside'
          value={checkDecimals && checkDecimals}
          onChange={(e: string) => handleConversionFactor(e)}
          readOnly={item.isCreatedAutomatically && !item.isApproved || !notBlocked || !endSecondRenderMapProduct}
        />
      </S.Cell>
      <S.Cell className="manufacturer" isAutomatic={item?.isCreatedAutomatically}>
        <Text showTip>{item.manufacturer}</Text>
      </S.Cell>
      <S.Cell className="subunidValue" isAutomatic={item?.isCreatedAutomatically}>
        <p>{item.subUnitValue ? currencyOutputNumber(item.subUnitValue ?? 0) : '-'}</p>
      </S.Cell>

      <VisibilityConditional
        condition={item.isCreatedAutomatically && !item.isApproved}
        firstOption={() => renderButtonsApprovedMaps()}
        secondOption={() => renderButtonsActions()}
      />
    </S.TRow>
  );
};

export default TRow;
